import type { CredentialState } from '@omni/shared';
import { zh } from '../i18n';
import { Button } from '../components/ui';
import { IconAlert } from '../components/icons';
import { useSession } from './SessionProvider';

/**
 * Credential + maintenance + update banners (SPEC §10, §11).
 *
 * The credential banner is driven by the 5-state machine of R11 and never
 * invents a state: `present` (set but not yet verified) is silent, because a
 * warning the user cannot act on is noise.
 */

const CREDENTIAL_MESSAGE: Partial<Record<CredentialState, { text: string; tone: 'warn' | 'error' }>> =
  {
    absent: { text: zh.banner.credentialAbsent, tone: 'warn' },
    invalid: { text: zh.banner.credentialInvalid, tone: 'error' },
    rate_limited: { text: zh.banner.credentialRateLimited, tone: 'warn' },
  };

export function CredentialBanner(): JSX.Element | null {
  const { credential } = useSession();
  if (!credential) return null;
  const entry = CREDENTIAL_MESSAGE[credential.state];
  if (!entry) return null;
  return (
    <div className={`banner banner--${entry.tone}`} role="status">
      <IconAlert size={18} />
      <span className="banner__text">{entry.text}</span>
    </div>
  );
}

export function MaintenanceBanner(): JSX.Element | null {
  const { maintenance } = useSession();
  if (!maintenance) return null;
  const text = maintenance.message
    ? zh.banner.maintenance(maintenance.message)
    : zh.banner.maintenanceDefault;
  return (
    <div className="banner banner--warn" role="status">
      <IconAlert size={18} />
      <span className="banner__text">{text}</span>
    </div>
  );
}

export function PendingRecreateBanner(): JSX.Element | null {
  const { pendingRecreate } = useSession();
  if (!pendingRecreate) return null;
  return (
    <div className="banner banner--info" role="status">
      <span className="banner__text">{zh.banner.pendingRecreate}</span>
    </div>
  );
}

/** SPEC §10 — `registerType:'prompt'`; never auto-reload mid-turn. */
export function UpdateBanner({
  visible,
  onUpdate,
  onDismiss,
}: {
  visible: boolean;
  onUpdate: () => void;
  onDismiss: () => void;
}): JSX.Element | null {
  if (!visible) return null;
  return (
    <div className="banner banner--info" role="status">
      <span className="banner__text">{zh.banner.updateReady}</span>
      <Button variant="outline" small onClick={onUpdate}>
        {zh.banner.update}
      </Button>
      <Button variant="ghost" small onClick={onDismiss}>
        {zh.banner.dismiss}
      </Button>
    </div>
  );
}
